<template>
  <MetricWidgetButton
    v-if="mention.audience"
    :aria-expanded="visible"
    aria-haspopup="menu"
    data-testid="broadcast-audience-trigger"
    :stacked="stacked"
    @click="handleClick"
    @keydown.esc="visible = false"
    @mouseout="menuOver = false"
    @mouseover="menuOver = true"
  >
    <div v-if="listView && !stacked">
      <div class="tw:flex tw:flex-nowrap">
        <BaseIcon
          class="tw:mr-1"
          icon="followers"
          style="font-size: 14px"
        />

        {{ audienceNumber }}
      </div>
    </div>

    <div
      v-else
      class="tw:flex tw:flex-nowrap"
      :class="{ 'tw:flex-col tw:gap-2': stacked }"
    >
      <div
        :class="{
          'tw:m-0 tw:flex tw:size-6 tw:items-center tw:p-0': stacked,
          'tw:mr-1 tw:pt-px': !stacked && dense,
          'tw:ml-1 tw:mr-2 tw:pt-1': !stacked && !dense,
        }"
      >
        <BaseIcon
          class="text-ds-denim-9"
          icon="followers"
          :style="{ 'font-size': dense ? '21px' : '18px' }"
        />
      </div>

      <div
        class="tw:text-denim-800"
        :class="{
          'tw:text-sm tw:leading-[1.4]': stacked,
          'tw:text-xxs': !stacked && dense,
          'tw:text-base tw:leading-5': !stacked && !dense,
        }"
      >
        {{ $t("broadcast_audience_widget.short_label") }}

        <div
          class="tw:text-left tw:font-bold"
          :class="{
            'tw:text-base': stacked,
            'tw:text-xxs': !stacked && dense,
          }"
        >
          {{ audienceNumber }}
        </div>
      </div>
    </div>

    <QMenu
      v-model="visible"
      no-parent-event
      @show="loadAudiences"
    >
      <div
        class="audience-widget-chart q-pa-sm q-gutter-md"
        data-testid="broadcast-audience-menu"
        @mouseout="contentOver = false"
        @mouseover="contentOver = true"
      >
        <MetricEditorInfoAlert show-audience-multiplier-info />
        <div class="flex-center row">
          <QIcon
            class="q-pr-xs"
            color="ds-denim-9"
            name="people"
            size="18px"
          />
          <span class="bold q-pr-xs">{{ suffix(mention.audience) }}</span>
          {{ $t("broadcast_audience_widget.potential_audience") }}
        </div>
        <QSpinner
          v-if="hasAudienceTimeline && loading"
          :size="30"
        />
        <ChartLibraryChart
          v-if="hasAudienceTimeline && loaded && audiences.length"
          v-bind="chartProps"
        />
        <span
          v-if="hasAudienceTimeline"
          class="text-help-text block text-center"
        >
          {{
            $t("broadcast_audience_widget.local_time", {
              location: mention.source.location,
              shortTimezone,
            })
          }}
        </span>
        <span v-if="hasAudienceTimeline && loaded && !audiences.length">{{
          $t("broadcast_audience_widget.no_data")
        }}</span>
      </div>
    </QMenu>
  </MetricWidgetButton>
</template>

<script>
import chroma from "chroma-js";
import { debounce, sortBy } from "lodash-es";

import { BaseIcon } from "shared/components/base";
import MetricWidgetButton from "shared/components/MetricWidgetButton.vue";
import { mediumColors } from "shared/helpers/colors";
import {
  addTimeZone,
  formatDate,
  parseDate,
  shortTimezone,
  toUTCTimezone,
} from "shared/helpers/date";
import { prettyNumber, suffix } from "shared/helpers/number";
import ChartLibraryChart from "shared/modules/Charts/components/ChartLibraryChart/ChartLibraryChart.vue";

import MetricEditorInfoAlert from "./user/MetricEditorInfoAlert.vue";

const POINT_RADIUS = 6;

export default {
  name: "BroadcastAudienceWidget",
  components: {
    BaseIcon,
    MetricWidgetButton,
    MetricEditorInfoAlert,
    ChartLibraryChart,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    medium: {
      type: String,
      required: true,
      validator(value) {
        return ["podcast", "radio", "tv"].includes(value);
      },
    },
    listView: {
      type: Boolean,
    },
    dense: {
      type: Boolean,
    },
    stacked: {
      type: Boolean,
    },
  },
  data() {
    return {
      visible: false,
      menuOver: false,
      contentOver: false,
      loading: false,
      loaded: false,
      audiences: [],
    };
  },
  computed: {
    chartProps() {
      return {
        chartData: { data: this.chartData, options: this.chartOptions },
        chartConfig: {
          component: "ShareOfVoiceTimeline",
          style: { width: "250px", height: "200px" },
          componentAttributes: {},
        },
      };
    },
    color() {
      return {
        podcast: mediumColors.podcast,
        radio: "#f98d5c",
        tv: "#f95858",
      }[this.medium];
    },
    hasAudienceTimeline() {
      return this.medium !== "podcast";
    },
    lineColor() {
      return chroma(this.color);
    },
    backgroundColor() {
      return this.lineColor.alpha(0.25);
    },
    shortTimezone() {
      return shortTimezone(
        this.mention.start_time,
        this.mention.source.time_zone
      );
    },
    chartData() {
      const datasets = [
        {
          borderColor: this.lineColor.css(),
          borderWidth: 2,
          backgroundColor: this.audiences.map((audienceBlock) =>
            audienceBlock.contains_mention
              ? this.lineColor.css()
              : this.backgroundColor.css()
          ),
          fill: true,
          data: this.audiences.map((audienceBlock) => audienceBlock.audience),
        },
      ];

      const labels = this.audiences.map((audienceBlock) => {
        const date = parseDate(audienceBlock.timestamp);
        const utcDate = toUTCTimezone(date);

        const dateInTimezone = addTimeZone(
          utcDate,
          this.mention.source.time_zone
        );

        return formatDate(dateInTimezone, "h:mmaaa");
      });

      return { labels, datasets };
    },
    chartOptions() {
      return {
        elements: {
          point: {
            radius: this.pointRadius,
            hoverRadius: this.pointRadius,
            display: true,
          },
        },
        scales: {
          x: {
            ticks: {
              callback(value) {
                return this.getLabelForValue(value);
              },
            },
          },
          y: {
            grid: { display: false },
            ticks: {
              callback: suffix,
            },
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItem) => tooltipItem[0].label,
            },
          },
        },
      };
    },
    audienceNumber() {
      if (this.listView) {
        return prettyNumber(this.mention.audience || "-");
      }

      return suffix(this.mention.audience);
    },
  },
  watch: {
    menuOver() {
      this.debouncedCheckVisible();
    },
    contentOver() {
      this.debouncedCheckVisible();
    },
  },
  mounted() {
    this.debouncedCheckVisible = debounce(this.checkVisible, 300);
  },
  beforeUnmount() {
    this.debouncedCheckVisible.cancel();
  },
  methods: {
    suffix,
    handleClick(event) {
      event.stopPropagation();

      this.visible = !this.visible;
    },
    checkVisible() {
      if (this.menuOver || this.contentOver) {
        this.visible = true;
      } else {
        this.visible = false;
      }
    },
    pointRadius(context) {
      return this.audiences[context.dataIndex].contains_mention
        ? POINT_RADIUS
        : 0;
    },
    async loadAudiences() {
      if (!this.hasAudienceTimeline || this.audiences.length) return;

      this.loading = true;

      try {
        const response = (
          await this.$streemApiV1.get(
            `${this.mention.type}s/${this.mention.id}/audiences`
          )
        ).data;

        this.audiences = sortBy(response, "timestamp");
      } catch {
        // do nothing for now...
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.audience-widget-chart {
  width: 276px;
}
</style>
